import { useState, type FormEvent } from 'react';
import { track } from '@/lib/analytics';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const TYPOLOGIES = ['Residential', 'Commercial', 'Hospitality', 'Other'];

const ENDPOINT = import.meta.env.VITE_INTAKE_ENDPOINT;
const FALLBACK_EMAIL = import.meta.env.VITE_FALLBACK_EMAIL ?? 'brianellisondesigns@icloud.com';

export function InquiryForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form));

    // Honeypot — bots fill hidden field
    if (data['_subject_line']) {
      setStatus('success');
      return;
    }

    track('inquiry_submitted', {
      typology: data['typology'],
      has_phone: Boolean(data['phone']),
      has_location: Boolean(data['location']),
    });

    try {
      if (ENDPOINT) {
        const res = await fetch(ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error(`Server returned ${res.status}`);
      } else {
        throw new Error('Intake endpoint not configured');
      }
      setStatus('success');
      form.reset();
    } catch (err) {
      setStatus('error');
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setErrorMessage(msg);
    }
  };

  if (status === 'success') {
    return (
      <div className="inquiry inquiry--success">
        <p className="eyebrow">Sent</p>
        <h3>Thank you — Brian will be in touch.</h3>
        <p>Most replies arrive within two business days.</p>
      </div>
    );
  }

  return (
    <form className="inquiry" onSubmit={handleSubmit} noValidate>
      <div className="inquiry__row">
        <label className="inquiry__field">
          <span className="eyebrow">Name</span>
          <input name="name" type="text" required autoComplete="name" />
        </label>
        <label className="inquiry__field">
          <span className="eyebrow">Email</span>
          <input name="email" type="email" required autoComplete="email" />
        </label>
      </div>
      <div className="inquiry__row">
        <label className="inquiry__field">
          <span className="eyebrow">Phone (optional)</span>
          <input name="phone" type="tel" autoComplete="tel" />
        </label>
        <label className="inquiry__field">
          <span className="eyebrow">Project typology</span>
          <select name="typology" defaultValue="">
            <option value="" disabled>Select…</option>
            {TYPOLOGIES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
      </div>
      <label className="inquiry__field">
        <span className="eyebrow">Location / city</span>
        <input name="location" type="text" autoComplete="address-level2" />
      </label>
      <label className="inquiry__field">
        <span className="eyebrow">Tell Brian about the project</span>
        <textarea name="message" rows={5} required />
      </label>

      {/* Honeypot — visually hidden */}
      <label className="visually-hidden" aria-hidden="true">
        Subject line
        <input type="text" name="_subject_line" tabIndex={-1} autoComplete="off" />
      </label>

      <div className="inquiry__actions">
        <button
          type="submit"
          className="cta-primary"
          disabled={status === 'submitting'}
        >
          {status === 'submitting' ? 'Sending…' : 'Send inquiry'}
        </button>
        {status === 'error' ? (
          <p className="caption inquiry__error">
            Could not send: {errorMessage}.&nbsp;
            <a href={`mailto:${FALLBACK_EMAIL}?subject=Project inquiry`} className="text-link">
              Email Brian directly instead
            </a>.
          </p>
        ) : null}
      </div>
    </form>
  );
}
